import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { SubscriptionPlan, SubscriptionPlanPrice, PromotionCode } from '../../../models/subscription/subscription.model';
import { LIMIT_LABELS, FEATURE_LABELS, COUNTRY_CURRENCY, COMING_SOON_FEATURES, formatLimitValue } from '../subscription-labels';

@Component({
  selector: 'cricteams-plan-card',
  standalone: true,
  imports: [CommonModule, CurrencyPipe],
  templateUrl: './plan-card.component.html',
  styleUrl: './plan-card.component.scss',
})
export class PlanCardComponent implements OnChanges {
  @Input() plan!: SubscriptionPlan;
  @Input() selectedInterval: 'Monthly' | 'Yearly' = 'Monthly';
  @Input() selectedCountry = 'US';
  @Input() isCurrentPlan = false;
  @Input() ctaLabel = 'Get Started';
  @Input() showCta = true;
  @Input() isPopular = false;
  @Input() ctaDisabled = false;
  @Input() ctaMuted = false;
  @Input() promotionCodes: PromotionCode[] = [];
  @Output() ctaClick = new EventEmitter<{ plan: SubscriptionPlan; priceId: string }>();

  matchedPrice: SubscriptionPlanPrice | null = null;
  matchedPromo: PromotionCode | null = null;
  promoCopied = false;
  currencyCode = 'USD';
  features: { key: string; label: string; enabled: boolean; comingSoon: boolean }[] = [];
  limits: { key: string; label: string; displayValue: string }[] = [];
  isFree = false;
  isUnlimited = false;

  ngOnChanges() {
    if (!this.plan) return;
    this.isFree = this.plan.level === 1;
    this.currencyCode = COUNTRY_CURRENCY[this.selectedCountry] || 'USD';
    this.matchedPrice = this.findPrice();
    this.matchedPromo = this.promotionCodes.find(
      pc => pc.planLevel === this.plan.level && pc.billingInterval === this.selectedInterval
    ) ?? null;
    this.parseFeatures();
    this.parseLimits();
  }

  private findPrice(): SubscriptionPlanPrice | null {
    if (this.isFree) return null;
    return (
      this.plan.prices.find(
        (p) =>
          p.billingInterval === this.selectedInterval &&
          p.country === this.selectedCountry
      ) ?? null
    );
  }

  private parseFeatures() {
    try {
      const parsed = JSON.parse(this.plan.featuresJson) as Record<string, boolean>;
      this.features = Object.entries(parsed).map(([key, enabled]) => ({
        key,
        label: FEATURE_LABELS[key] || key,
        enabled,
        comingSoon: COMING_SOON_FEATURES.has(key),
      }));
    } catch {
      this.features = [];
    }
  }

  private parseLimits() {
    try {
      const parsed = JSON.parse(this.plan.limitsJson) as Record<string, number>;
      const hasLimits = Object.keys(parsed).length > 0;
      this.isUnlimited = !this.isFree && !hasLimits;

      if (this.isUnlimited) {
        // Premium: compact "Everything unlimited" block — no individual rows needed
        this.limits = [];
      } else {
        this.limits = Object.entries(parsed)
          .filter(([key]) => LIMIT_LABELS[key])
          .map(([key, value]) => ({
            key,
            label: LIMIT_LABELS[key],
            displayValue: value === 0 ? '—' : formatLimitValue(key, value),
          }));
      }
    } catch {
      this.limits = [];
      this.isUnlimited = false;
    }
  }

  copyPromoCode(event: Event) {
    event.stopPropagation();
    if (!this.matchedPromo) return;
    navigator.clipboard.writeText(this.matchedPromo.code).then(() => {
      this.promoCopied = true;
      setTimeout(() => (this.promoCopied = false), 2000);
    });
  }

  onCtaClick() {
    this.ctaClick.emit({
      plan: this.plan,
      priceId: this.matchedPrice?.id ?? '',
    });
  }
}
